/**
 * @architecture-header 1
 * @layer module
 * @domain authentication
 * @responsibility Bootstrap the backend identity projection and expose the current in-memory workspace choice.
 * @state-owner TanStack Query
 * @allowed-imports app environment/session; contracts; TanStack Query
 * @forbidden localStorage; Zustand; permission inference; generated SDK direct imports
 * @public-api auth module
 * @side-effects idempotent authenticated identity bootstrap
 * @naming-hook GET -> use<Resource> (no verb); POST create -> useCreate<Resource>; action sub-resource -> use<Action><Resource>
 * @naming-hook PUT/PATCH entity -> useUpdate<Resource>; DELETE -> useDelete<Resource>
 * @naming-hook banned prefixes: useGet, useFetch, useLoad, useSet, useRemove, useSave, useDo
 * @naming-path plural lowercase nouns; /me for the caller; no version, role or verb segment; filters in the query string
 * @naming-body camelCase; never the caller's role and never a server-side key — the token says who, the path says what
 * @naming-rules docs/architecture/API_NAMING.md, all three enforced by pnpm api-naming:check
 * @tests module-contracts.test.ts
 * @security-boundary authenticated FastAPI context response
 * @data-classification membership role and account scope
 * @trust-model backend memberships are authoritative; the in-memory choice grants no authority
 */

import { useQuery } from '@tanstack/react-query';
import type { ActiveContext } from '@vispo/contracts';
import { useWorkspaceEnvironment } from '../../../app/config/environment';
import { useWorkspaceSession } from '../../../app/providers/SessionProvider';
import { bootstrapMe } from './identity-context';

export const authKeys = {
  all: ['auth'] as const,
  activeContext: (userId: string) => [...authKeys.all, 'active-context', userId] as const,
};

function isInvalidSession(cause: unknown): boolean {
  return Boolean(cause && typeof cause === 'object' && 'status' in cause && cause.status === 401);
}

export function useActiveContext() {
  const environment = useWorkspaceEnvironment();
  const { session, signOut } = useWorkspaceSession();
  const userId = session?.userId ?? 'anonymous';

  return useQuery({
    queryKey: authKeys.activeContext(userId),
    enabled: Boolean(session),
    queryFn: async (): Promise<ActiveContext | null> => {
      if (!session || environment.demoMode) return null;
      try {
        await bootstrapMe(environment.apiClient);
      } catch (cause) {
        if (isInvalidSession(cause)) await signOut();
        throw cause;
      }
      return null;
    },
    staleTime: Number.POSITIVE_INFINITY,
  });
}
